import * as React from 'react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface QuestionnaireFooterProps {
    onBack?: () => void;
    onContinue?: () => void;
    backLabel?: string;
    continueLabel?: string;
    /** Hidden on the opening step, where there is nowhere to go back to. */
    showBack?: boolean;
    /** Outline matches checkout Back; primary matches terminal CTAs such as Return to Dashboard. */
    backVariant?: 'outline' | 'primary';
    /** Hidden on terminal steps that only offer a back action, e.g. purchase complete. */
    showContinue?: boolean;
    /** Continue is disabled until the step's answer satisfies its rule. */
    canContinue?: boolean;
    /** Suppresses both actions while a submit is in flight. */
    busy?: boolean;
    /**
     * Drops Back and Continue but keeps the rule and the legal row, for screens
     * that carry their own actions — the account wall and the outcomes.
     */
    hideActions?: boolean;
    /** Legal row under the rule. Defaults to copyright, Terms, Privacy Policy. */
    legal?: React.ReactNode;
    className?: string;
}

const PILL_BASE =
    'inline-flex h-10 items-center justify-center rounded-[var(--radius-btn-m)] px-4.5 font-sans text-3.75 leading-[1.07] tracking-[-0.03em] transition-colors disabled:cursor-not-allowed';

export const QuestionnaireFooter = ({
    onBack,
    onContinue,
    backLabel = 'Back',
    continueLabel = 'Continue',
    showBack = true,
    backVariant = 'outline',
    showContinue = true,
    canContinue = true,
    busy = false,
    hideActions = false,
    legal,
    className,
}: QuestionnaireFooterProps) => (
    <div className={cn('flex flex-col', className)}>
        {hideActions ? null : (
            // Inset from the pane's own gutter, in pixels rather than a
            // spacing class: this modal's rem is under 16px, so `px-5` here
            // would render 16px and read as 20. Desktop only — on a phone the
            // primary pill is flex-1 and wants the full gutter. The rule and
            // the legal row stay on the pane's edge, so the actions sit a
            // little inside the line that closes them off.
            <div className="flex items-center justify-between gap-3 md:px-[17px]">
                {showBack ? (
                    <button
                        className={cn(
                            PILL_BASE,
                            'disabled:opacity-40',
                            backVariant === 'primary'
                                ? cn(
                                      'text-white shadow-[0_2.167px_7.583px_rgba(0,0,0,0.11)]',
                                      QUESTIONNAIRE_COLOUR.actionPrimary,
                                      'hover:bg-[#181818]',
                                  )
                                : cn(
                                      'border border-solid',
                                      QUESTIONNAIRE_COLOUR.actionOutline,
                                      QUESTIONNAIRE_COLOUR.actionOutlineInk,
                                      'hover:bg-[#f6f6f6]',
                                  ),
                        )}
                        disabled={busy}
                        onClick={onBack}
                        type="button"
                    >
                        {backLabel}
                    </button>
                ) : showContinue ? (
                    // Holds the Continue pill on the right when Back is absent.
                    <span aria-hidden="true" />
                ) : null}

                {showContinue ? (
                    <button
                        className={cn(
                            PILL_BASE,
                            'flex-1 text-white shadow-[0_2.167px_7.583px_rgba(0,0,0,0.11)] disabled:opacity-40 md:min-w-38.5 md:flex-none',
                            QUESTIONNAIRE_COLOUR.actionPrimary,
                            'hover:bg-[#181818]',
                        )}
                        disabled={busy || !canContinue}
                        onClick={onContinue}
                        type="button"
                    >
                        {continueLabel}
                    </button>
                ) : null}
            </div>
        )}

        {/* The rule closes the actions off; it is not a border they sit on.
            At mt-0.75 the primary pill's shadow reached it and Back looked
            welded to the legal row underneath. */}
        <hr
            className={cn(
                'w-full border-t',
                hideActions ? 'mt-0' : 'mt-5',
                QUESTIONNAIRE_COLOUR.hairline,
            )}
        />

        <div
            className={cn(
                'mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 font-sans text-3 leading-[1.3] tracking-[-0.02em] md:gap-x-7',
                QUESTIONNAIRE_COLOUR.inkMuted,
            )}
        >
            {legal ?? (
                <>
                    <span>© ArchiPro {new Date().getFullYear()}</span>
                    <span className="w-3"></span>
                    <a className="hover:underline" href="/terms-of-use">
                        Terms
                    </a>
                    <a className="hover:underline" href="/privacy-policy">
                        Privacy Policy
                    </a>
                </>
            )}
        </div>
    </div>
);
